// Ollama Provider (Unified Interface)

import type { OllamaClient } from '../../client/ollama/index.js'
import type {
  ChatArgs,
  ProviderAdapter,
  UnifiedChatResponse,
  UnifiedConfig,
  UnifiedMessage,
  UnifiedToolCall,
  UnifiedToolDefinition,
} from './types.js'

const convertMessages = (messages: UnifiedMessage[]): unknown[] => {
  return messages.map(msg => {
    if (msg.role === 'tool') {
      return { role: 'tool', content: msg.content, tool_call_id: msg.toolCallId }
    }

    const converted: Record<string, unknown> = { role: msg.role, content: msg.content || '' }

    if (msg.toolCalls && msg.toolCalls.length > 0) {
      converted.tool_calls = msg.toolCalls.map(tc => ({
        id: tc.id,
        type: tc.type || 'function',
        function: {
          name: tc.function.name,
          arguments:
            typeof tc.function.arguments === 'string'
              ? tc.function.arguments
              : JSON.stringify(tc.function.arguments),
        },
      }))
    }

    return converted
  })
}

const convertTools = (tools?: UnifiedToolDefinition[]): unknown[] | undefined => {
  if (!tools || tools.length === 0) return undefined

  return tools.map(tool => ({
    type: 'function',
    function: {
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters,
    },
  }))
}

const parseToolArguments = (argsString: unknown): Record<string, unknown> => {
  try {
    return typeof argsString === 'string'
      ? JSON.parse(argsString)
      : (argsString as Record<string, unknown>)
  } catch (_error) {
    console.warn('[Ollama] Failed to parse tool arguments:', argsString)
    return {}
  }
}

export function createOllamaAdapter(ollamaClient: OllamaClient): ProviderAdapter {
  const chat = async ({
    model,
    messages,
    tools,
    config = {},
  }: ChatArgs): Promise<UnifiedChatResponse> => {
    const host = config.host || process.env.OLLAMA_HOST || 'localhost:11434'

    try {
      const request = {
        host,
        model: model as string,
        messages: convertMessages(messages),
        tools: convertTools(tools),
        think: false,
        options: {
          temperature: config.temperature || 0.7,
          num_predict: config.maxTokens || 2000,
        },
        stream: false,
      }
      console.log('[Ollama] request:', JSON.stringify(request, null, 2))
      const response: any = await ollamaClient.chat(request)

      console.log('[Ollama] response:', JSON.stringify(response, null, 2))
      const msg = response.message
      if (!msg) {
        throw new Error('Ollama returned no message')
      }

      const toolCalls: UnifiedToolCall[] | undefined = msg.tool_calls?.map((tc: any) => ({
        id: tc.id || `call_${Math.random().toString(36).slice(2, 11)}`,
        type: tc.type || 'function',
        function: {
          name: tc.function.name,
          arguments: parseToolArguments(tc.function.arguments),
        },
      }))

      const finishReason = msg.tool_calls?.length > 0 ? 'tool_calls' : 'stop'

      return {
        message: {
          role: 'assistant',
          content: msg.content || '',
          toolCalls: toolCalls || [],
          finishReason,
          usage: {
            promptTokens: response.prompt_eval_count || 0,
            completionTokens: response.eval_count || 0,
            totalTokens: (response.prompt_eval_count || 0) + (response.eval_count || 0),
          },
        },
        raw: response,
      }
    } catch (error: any) {
      throw new Error(`Ollama chat failed: ${error.message}`, { cause: error })
    }
  }

  const listModels = async ({ config = {} }: { apiKey?: string; config?: UnifiedConfig } = {}) => {
    const host = config?.host || process.env.OLLAMA_HOST

    try {
      const response: any = await ollamaClient.listModels({ host })
      const models = response.models || []

      return models.map((m: any) => ({
        id: m.name,
        name: m.name,
        size: m.size,
        modified: m.modified_at,
        details: m.details,
      }))
    } catch (error: any) {
      throw new Error(`Failed to list Ollama models: ${error.message}`, { cause: error })
    }
  }

  const checkAvailability = async (config: UnifiedConfig = {}): Promise<boolean> => {
    const host = config?.host || process.env.OLLAMA_HOST
    return await ollamaClient.checkAvailability(host)
  }

  return { chat, listModels, checkAvailability }
}
